import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Loader from '@/components/Loader';
import { useGetWorkOrderById } from '@/services/workOrderService';
import { getProcessBillingServer, isProcessBillingNotFoundError } from '@/services/processBillingServerService';
import { isProcessBillingServerEligible } from '@/pages/caseManagement/processBilling/processBillingServerUtils';
import { isProcessBillingVendorEligible } from '@/pages/caseManagement/processBilling/processBillingVendorUtils';
import { useUserStore } from '@/stores/user';
import { isAdminOrSupervisor as isAdminOrSupervisorUser } from '@/services/userService';
import { useQuery } from '@tanstack/react-query';
import { Edit, FileText, Plus, Send, User } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { OneLineText } from '@/components/OneLineText';
import { DocumentListDialog } from '@/components/DocumentListDialog';
import { useMemo, useState } from 'react';
import type { DocumentListType, MediaBaseResponse } from '@/types/types';
import type { ProcessBillingDocumentResponse } from '@/types/processBillingServer.types';
import { SendAffidavitConfirmDialog } from '@/pages/caseManagement/processBilling/SendAffidavitConfirmDialog';

const PROOF_OF_SERVICE_HELPER = 'Proof of Service is required. Upload one or more PDF files (max 20 MB each).';

export const ProcessBillingServerView = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);

  const woQuery = useGetWorkOrderById(workOrderId);
  const billingQuery = useQuery({
    queryKey: ['processBillingServer', workOrderId],
    queryFn: () => getProcessBillingServer(workOrderId!),
    enabled: Boolean(workOrderId),
  });

  const canEdit = isAdminOrSupervisorUser(user);
  const billingData = billingQuery.data;
  const hasBilling = Boolean(billingData?.processBillingBatchId);

  const allDocuments = useMemo<DocumentListType[]>(() => {
    if (!billingData) return [];
    return billingData.billingEntries.flatMap((entry) =>
      entry.proofOfService.map((doc) => ({
        name: doc.name,
        media: { mediaId: doc.mediaId } as MediaBaseResponse,
        affidavitSendDocumentId: doc.processBillingDocumentId,
      }))
    );
  }, [billingData]);

  const [openDocumentDialog, setOpenDocumentDialog] = useState(false);
  const [activeDocumentMediaId, setActiveDocumentMediaId] = useState<string | undefined>();
  const activeDocumentIndex = useMemo(() => {
    if (!activeDocumentMediaId) return 0;
    const idx = allDocuments.findIndex((d) => d.media?.mediaId === activeDocumentMediaId);
    return idx >= 0 ? idx : 0;
  }, [allDocuments, activeDocumentMediaId]);

  const [pendingSendServerDocumentId, setPendingSendServerDocumentId] = useState<string | null>(null);

  const openDocument = (doc: ProcessBillingDocumentResponse) => {
    setActiveDocumentMediaId(doc.mediaId);
    setOpenDocumentDialog(true);
  };

  const woNumber = woQuery.data?.number ?? '';
  const wo = woQuery.data;
  const serverEligible = Boolean(wo && isProcessBillingServerEligible(wo));
  const vendorEligible = Boolean(wo && isProcessBillingVendorEligible(wo));
  const billingLoadFailed = billingQuery.isError && !isProcessBillingNotFoundError(billingQuery.error);
  const noServerBatchYet =
    !hasBilling &&
    (!billingQuery.isError || (billingQuery.isError && isProcessBillingNotFoundError(billingQuery.error)));

  const goEdit = () => navigate(`/process/workorder/${workOrderId}/process-billing/edit`);
  const goCreate = () => navigate(`/process/workorder/${workOrderId}/process-billing/create`);
  const goVendorCreate = () => navigate(`/process/workorder/${workOrderId}/process-billing-vendor/create`);
  const goVendorView = () => navigate(`/process/workorder/${workOrderId}/process-billing-vendor`);

  return (
    <Loader isLoading={woQuery.isFetching || billingQuery.isFetching}>
      <div className="w-full h-full min-w-[170px] flex flex-col gap-6">
        <div className="flex flex-row items-center justify-between">
          <h1 className="text-2xl font-semibold text-primary tracking-tight">{`W/O: ${woNumber} - Billing Entry`}</h1>
          {hasBilling && canEdit ? (
            <button
              type="button"
              aria-label="Edit billing"
              className="bg-white border border-bordercolor rounded-md p-1.5 hover:bg-[#EDF5FF]"
              onClick={goEdit}
            >
              <Edit className="h-5 w-5 text-placeholder" />
            </button>
          ) : null}
        </div>

        {billingLoadFailed ? (
          <div className="text-sm text-destructive">Could not load process billing for this work order.</div>
        ) : null}

        {!billingQuery.isFetching && noServerBatchYet ? (
          vendorEligible && !serverEligible ? (
            <div className="flex flex-col items-start gap-3 rounded-lg bg-[#F5F5F5] p-6 text-sm max-w-xl">
              <span className="text-placeholder">
                No server billing batch applies to this work order. It is set up for vendor process billing—add or view
                the vendor billing entry instead.
              </span>
              <div className="flex flex-wrap gap-3">
                <Button type="button" onClick={goVendorCreate}>
                  Add Vendor Billing
                </Button>
                <Button type="button" variant="outline" className="border-primary text-primary" onClick={goVendorView}>
                  View vendor billing
                </Button>
              </div>
            </div>
          ) : serverEligible ? (
            <div className="flex flex-col items-start gap-3 rounded-lg bg-[#F5F5F5] p-6 text-sm">
              <span className="text-placeholder">No billing entry on file for this work order yet.</span>
              <Button type="button" onClick={goCreate}>
                Add Server Billing
              </Button>
              {vendorEligible ? (
                <p className="text-xs text-placeholder max-w-xl">
                  This work order also has a vendor assigned. For vendor payment, use{' '}
                  <button type="button" className="text-primary underline font-medium" onClick={goVendorView}>
                    vendor billing
                  </button>
                  .
                </p>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-destructive">
              This work order is not eligible for server or vendor process billing from this screen.
            </div>
          )
        ) : null}

        {hasBilling && billingData ? (
          <Tabs defaultValue="0">
            <TabsList className="bg-[#F5F5F5] h-12 w-full justify-start rounded-none p-0 border-b border-bordercolor flex overflow-x-auto scrollbar-hide">
              {billingData.billingEntries.map((entry, idx) => (
                <TabsTrigger
                  key={entry.processBillingServerEntryId}
                  value={String(idx)}
                  className="h-12 px-3 gap-2 rounded-none border-r border-bordercolor shrink-0 text-sm font-medium text-primary data-[state=active]:bg-[#EDF5FF] data-[state=inactive]:bg-[#E2E2E2] data-[state=active]:shadow-none"
                >
                  <User className="h-5 w-5" />
                  <span>{entry.serverName}</span>
                </TabsTrigger>
              ))}
              <button
                type="button"
                aria-label="Add server (managed via work order assignment)"
                disabled
                className="h-12 w-12 shrink-0 bg-primary text-white flex items-center justify-center cursor-not-allowed"
              >
                <Plus className="h-5 w-5" />
              </button>
            </TabsList>
            {billingData.billingEntries.map((entry, idx) => (
              <TabsContent key={entry.processBillingServerEntryId} value={String(idx)} className="mt-6 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm font-medium">Server ID</Label>
                    <Input
                      value={entry.serverCode ?? '—'}
                      readOnly
                      disabled
                      className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm font-medium">Server&apos;s Name</Label>
                    <Input
                      value={entry.serverName}
                      readOnly
                      disabled
                      className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm font-medium">Income</Label>
                    <Input
                      value={formatCurrency(entry.income)}
                      readOnly
                      disabled
                      className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm font-medium">Expense</Label>
                    <Input
                      value={formatCurrency(entry.expense)}
                      readOnly
                      disabled
                      className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-sm font-medium">Total</Label>
                    <Input
                      value={formatCurrency(entry.total)}
                      readOnly
                      disabled
                      className="bg-[#F5F5F5] border-bordercolor text-placeholder disabled:opacity-100 disabled:cursor-default"
                    />
                  </div>
                  <div aria-hidden className="hidden md:block" />
                </div>

                <div className="mt-8 rounded-lg bg-[#F5F5F5] p-5">
                  <div className="flex flex-row justify-between items-start mb-4">
                    <div>
                      <div className="text-base font-semibold text-primary">Proof of Service</div>
                      <p className="text-sm text-placeholder italic">{PROOF_OF_SERVICE_HELPER}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    {entry.proofOfService.map((doc) => (
                      <div
                        key={doc.processBillingDocumentId}
                        className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end"
                      >
                        <div className="flex flex-col gap-1">
                          <Label className="text-sm font-medium">Document Name</Label>
                          <Input
                            value={doc.name}
                            readOnly
                            disabled
                            className="bg-white border-bordercolor text-[#0A0A0A] disabled:opacity-100 disabled:cursor-default"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium invisible select-none">spacer</span>
                          <div
                            className="flex flex-row items-center gap-2 rounded-md bg-white border border-dashed border-[#92A1B3] px-4 h-12 cursor-pointer"
                            onClick={() => openDocument(doc)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openDocument(doc);
                              }
                            }}
                          >
                            <FileText className="h-5 w-5 text-primary shrink-0" />
                            <OneLineText text={doc.name} className="flex-1 text-sm text-primary" />
                            <button
                              type="button"
                              aria-label={`Send ${doc.name}`}
                              className="text-primary hover:text-[#12375C]"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingSendServerDocumentId(doc.processBillingDocumentId);
                              }}
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        ) : null}

        <div className="flex flex-row gap-3 justify-end mt-6">
          <Button
            type="button"
            variant="outline"
            className="w-[125px] h-[48px] border-primary text-primary hover:bg-[#EDF5FF]"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>
      </div>
      {openDocumentDialog && allDocuments.length > 0 ? (
        <DocumentListDialog
          documents={allDocuments}
          activeIndex={activeDocumentIndex}
          onCloseClick={() => setOpenDocumentDialog(false)}
          onRequestSendAffidavit={(id) => setPendingSendServerDocumentId(id)}
        />
      ) : null}
      {pendingSendServerDocumentId && workOrderId ? (
        <SendAffidavitConfirmDialog
          workOrderId={workOrderId}
          documentIds={[pendingSendServerDocumentId]}
          clientId={woQuery.data?.service?.client?.clientId ?? null}
          onClose={() => setPendingSendServerDocumentId(null)}
        />
      ) : null}
    </Loader>
  );
};
